import React, { useState } from 'react'
import { AlertTriangle, CircleCheck, Loader2, ChevronRight, Check, Search, BookOpen, FilePen, Layers, Inbox } from 'lucide-react'
import { DocumentResultCard, pickDocumentResultDisplayTitle } from '@/components/ui/document-result-card'
import { FileEditWidget } from '@/components/features/file-edit-widget'
import { StreamingOutputWindow } from '@/components/features/chat-messages/streaming-output-window'
import type { Message } from '@/components/layout/chat-pane.types'
import { getToolLabel } from '@/components/layout/chat-pane.utils'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import {
  isAllowlistSoftFailure,
  isHardToolFailure,
  isSoftToolFailure,
  softFailureDisplayText,
  toolResultFailureText,
} from '@/components/features/chat-messages/tool-message.soft-failure'
import { ImageSearchGallery, type ImageSearchHit } from '@/components/features/chat-messages/image-search-gallery'
import { QuizCreatedCard } from '@/components/features/chat-messages/quiz-created-card'

export {
  isAllowlistSoftFailure,
  isHardToolFailure,
  isSoftToolFailure,
  softFailureDisplayText,
  toolResultFailureText,
} from '@/components/features/chat-messages/tool-message.soft-failure'

/** Tools whose full `toolResult` JSON is huge or redundant in chat — show a one-line footnote instead of expandable raw JSON. */
export function suppressVerboseToolDetailsJson(
  toolName: string | undefined,
  toolResult: unknown
): boolean {
  if (!toolName || !toolResult || typeof toolResult !== 'object') return false
  const tr = toolResult as { success?: boolean }
  if (!tr.success) return false
  if (toolName === 'readToolResult') return true
  if (toolName === 'searchDocuments') return true
  if (toolName === 'getDocument') return true
  if (toolName === 'fetchBasecampTodo') return true
  if (toolName === 'updateBasecampTodoNotes') return true
  if (toolName === 'getTodoContext') return true
  if (toolName === 'imageSearch') return true
  if (toolName === 'createQuizDocument') return true
  return false
}

/** One-line summary when verbose JSON is suppressed (keeps triage / agentic runs scannable). */
export function compactToolFootnote(toolName: string | undefined, toolResult: unknown): string | null {
  if (!toolName || !toolResult || typeof toolResult !== 'object') return null
  const tr = toolResult as Record<string, unknown>

  if (toolName === 'readToolResult') {
    const st =
      (typeof tr.size_tokens === 'number' ? tr.size_tokens : undefined) ??
      (typeof (tr.result as Record<string, unknown> | undefined)?.size_tokens === 'number'
        ? ((tr.result as Record<string, unknown>).size_tokens as number)
        : undefined)
    const inner = tr.result as Record<string, unknown> | undefined
    const doc =
      (inner?.document as Record<string, unknown> | undefined) ??
      ((inner?.result as Record<string, unknown> | undefined)?.document as
        | Record<string, unknown>
        | undefined)
    const title = typeof doc?.title === 'string' ? doc.title : undefined
    const path = typeof doc?.path === 'string' ? doc.path : undefined
    const bits: string[] = []
    if (title) bits.push(title)
    if (path) bits.push(path)
    if (typeof st === 'number') bits.push(`~${st} tokens`)
    return bits.length > 0 ? bits.join(' — ') : 'Spilled tool result loaded'
  }

  if (toolName === 'searchDocuments') {
    const docs = tr.documents ?? tr.results ?? tr.hits
    const n =
      typeof tr.count === 'number'
        ? tr.count
        : Array.isArray(docs)
          ? docs.length
          : undefined
    const fa = tr.filtersApplied as Record<string, unknown> | undefined
    const q =
      typeof tr.query === 'string'
        ? tr.query
        : typeof fa?.query === 'string'
          ? (fa.query as string)
          : undefined
    if (typeof n === 'number') {
      const qShort = q ? (q.length > 56 ? `${q.slice(0, 56)}…` : q) : ''
      return qShort ? `“${qShort}” · ${n} match${n === 1 ? '' : 'es'}` : `${n} match${n === 1 ? '' : 'es'}`
    }
    return 'Search complete'
  }

  if (toolName === 'imageSearch') {
    const n = Array.isArray(tr.results) ? tr.results.length : 0
    const q = typeof tr.query === 'string' ? tr.query : ''
    const qShort = q ? (q.length > 56 ? `${q.slice(0, 56)}…` : q) : ''
    return qShort
      ? `“${qShort}” · ${n} image${n === 1 ? '' : 's'}`
      : `${n} image${n === 1 ? '' : 's'}`
  }

  if (toolName === 'createQuizDocument') {
    const title = typeof tr.title === 'string' ? tr.title : 'Quiz'
    const n = typeof tr.questionCount === 'number' ? tr.questionCount : null
    return n != null ? `${title} · ${n} questions` : title
  }

  if (toolName === 'getDocument') {
    const doc = tr.document as Record<string, unknown> | undefined
    const title = typeof doc?.title === 'string' ? doc.title : undefined
    const displayPath =
      typeof tr.displayPath === 'string'
        ? tr.displayPath
        : typeof doc?.displayPath === 'string'
          ? (doc.displayPath as string)
          : undefined
    const rawPath =
      typeof doc?.path === 'string'
        ? doc.path
        : typeof tr.filePath === 'string'
          ? tr.filePath
          : undefined
    const pathHint = displayPath || (rawPath ? rawPath.split('/').pop() : undefined)
    if (title || pathHint) return [title, pathHint].filter(Boolean).join(' — ')
    return 'Document opened'
  }

  if (toolName === 'getTodoContext') {
    const n = typeof tr.count === 'number' ? tr.count : Array.isArray(tr.documents) ? tr.documents.length : undefined
    const path =
      typeof (tr.documents as Record<string, unknown>[] | undefined)?.[0]?.filePath === 'string'
        ? ((tr.documents as Record<string, unknown>[])[0].filePath as string)
        : undefined
    if (typeof n === 'number' && n > 0) {
      const short = path ? path.split('/').slice(-2).join('/') : ''
      return short ? `${n} doc${n === 1 ? '' : 's'} - ${short}` : `${n} linked doc${n === 1 ? '' : 's'}`
    }
    if (tr.error || tr.message) {
      return String(tr.error || tr.message).slice(0, 80)
    }
    return null
  }

  if (toolName === 'updateDocumentFrontmatter') {
    const fields = tr.updatedFields as Record<string, unknown> | undefined
    const keys = fields && typeof fields === 'object' ? Object.keys(fields) : []
    const docId = typeof tr.documentId === 'string' ? tr.documentId.slice(0, 8) : ''
    if (keys.length > 0) return `Updated ${keys.join(', ')}${docId ? ` (${docId}…)` : ''}`
    return 'Frontmatter updated'
  }

  if (toolName === 'updateBasecampTodoNotes') {
    const title =
      typeof tr.title === 'string'
        ? tr.title
        : typeof (tr.todo as Record<string, unknown> | undefined)?.title === 'string'
          ? ((tr.todo as Record<string, unknown>).title as string)
          : undefined
    const proj =
      typeof tr.project_name === 'string'
        ? tr.project_name
        : typeof (tr.todo as Record<string, unknown> | undefined)?.project === 'object' &&
            typeof ((tr.todo as Record<string, unknown>).project as Record<string, unknown>)?.name ===
              'string'
          ? (((tr.todo as Record<string, unknown>).project as Record<string, unknown>).name as string)
          : undefined
    const due = typeof tr.due_on === 'string' ? tr.due_on : undefined
    const fields = Array.isArray(tr.updated_fields) ? (tr.updated_fields as string[]).join(', ') : ''
    const bits: string[] = []
    if (fields) bits.push(fields)
    if (due) bits.push(`due ${due}`)
    if (title) bits.push(title.length > 48 ? `${title.slice(0, 47)}…` : title)
    if (proj) bits.push(proj)
    return bits.length > 0 ? bits.join(' · ') : 'Updated'
  }

  if (toolName === 'fetchBasecampTodo') {
    const todo = tr.todo as Record<string, unknown> | undefined
    const title =
      typeof tr.title === 'string'
        ? tr.title
        : typeof todo?.title === 'string'
          ? (todo.title as string)
          : undefined
    const comments = tr.comments
    const n =
      typeof tr.commentCount === 'number'
        ? tr.commentCount
        : Array.isArray(comments)
          ? comments.length
          : undefined
    const latest =
      typeof tr.latestCommentAt === 'string' ? tr.latestCommentAt.slice(0, 10) : undefined
    const bits: string[] = []
    if (title) bits.push(title)
    if (typeof n === 'number') bits.push(`${n} comment${n === 1 ? '' : 's'}`)
    if (latest) bits.push(`latest ${latest}`)
    if (bits.length > 0) return bits.join(' — ')
    return 'Basecamp todo loaded'
  }

  return null
}

export interface BatchToolMessagesProps {
  messages: Message[]
  onDocumentClick?: (docId: string, event?: React.MouseEvent) => void | Promise<void>
  activeDocumentId?: string | null
}

const isBasecampTool = (n: string) =>
  n.toLowerCase().includes('basecamp') || n.includes('Todo') || n.includes('Assignment')
const isSearchTool = (n: string) =>
  n === 'searchDocuments' || n === 'semanticSearch' || n === 'searchYoutube' || n === 'searchBasecamp' || n === 'queryBasecampTodos' || n === 'getBasecampAssignments'
const isReadTool = (n: string) =>
  n === 'getDocument' || n === 'readToolResult' || n === 'fetchBasecampTodo' || n === 'getBasecampTodoComments' || n === 'fetchYoutubeTranscript'
const isWriteTool = (n: string) =>
  n === 'createDocument' || n === 'updateDocument' || n === 'appendToDocument' || n === 'updateDocumentFrontmatter' || n === 'moveDocument' || n === 'archiveDocument' || n === 'updateBasecampTodoNotes'

/**
 * True when two tool names belong to the same batchable group (consecutive similar steps).
 * Mixed categories (e.g. search then write) are not batched together.
 */
export function toolMessagesShareBatchGroup(a?: string, b?: string): boolean {
  if (!a || !b) return false
  if (a === b) return true
  if (isSearchTool(a) && isSearchTool(b)) return true
  if (isReadTool(a) && isReadTool(b)) return true
  if (isWriteTool(a) && isWriteTool(b)) return true
  if (isBasecampTool(a) && isBasecampTool(b)) return true
  return false
}

/** Returns the dominant tool category icon for a batch header. */
function batchHeaderIcon(toolNames: (string | undefined)[]): React.ReactNode {
  const defined = toolNames.filter(Boolean) as string[]
  const anyBasecampInbox = defined.some(n => n === 'fetchBasecampTodo' || n === 'getBasecampTodoComments' || n === 'getBasecampAssignments' || n === 'queryBasecampTodos')
  if (defined.every(isSearchTool)) return <Search className="h-3.5 w-3.5" aria-hidden />
  if (defined.every(isReadTool) && anyBasecampInbox) return <Inbox className="h-3.5 w-3.5" aria-hidden />
  if (defined.every(isReadTool)) return <BookOpen className="h-3.5 w-3.5" aria-hidden />
  if (defined.every(isWriteTool)) return <FilePen className="h-3.5 w-3.5" aria-hidden />
  if (defined.some(isBasecampTool)) return <Inbox className="h-3.5 w-3.5" aria-hidden />
  return <Layers className="h-3.5 w-3.5" aria-hidden />
}

/** Tool run finished (success or failure). Do not treat `{ success: false, message }` as still running. */
export function isToolRunTerminal(toolResult: unknown): boolean {
  if (toolResult == null || typeof toolResult !== 'object') return false
  const tr = toolResult as Record<string, unknown>
  if (tr.success === true) return true
  if (tr.success === false) return true
  if (toolResultFailureText(toolResult)) return true
  // Reloaded toolTrace rows may be truncated to `{ preview }` without success — still finished.
  if (tr.truncated === true) return true
  if (typeof tr.preview === 'string' && tr.preview.length > 0) return true
  // Empty `{}` from a broken tool envelope — treat as terminal failure, not infinite "Running"
  if (Object.keys(tr).length === 0) return true
  return false
}

/** Produce a compact human-readable label for a group of tool calls, e.g. "5 Basecamp lookups". */
function buildGroupLabel(messages: Message[]): string {
  const total = messages.length
  const toolNames = messages.map(m => m.toolName).filter(Boolean) as string[]
  if (toolNames.length === 0) return `${total} operation${total === 1 ? '' : 's'}`

  const allBasecamp = toolNames.every(isBasecampTool)
  const allSearch = toolNames.every(isSearchTool)
  const allWrite = toolNames.every(isWriteTool)
  const allRead = toolNames.every(isReadTool)

  if (allBasecamp) return `${total} Basecamp lookup${total === 1 ? '' : 's'}`
  if (allSearch) return `${total} search${total === 1 ? '' : 'es'}`
  if (allWrite) {
    const bcUpdates = toolNames.filter((n) => n === 'updateBasecampTodoNotes').length
    if (bcUpdates === total) return `${total} Basecamp due-date update${total === 1 ? '' : 's'}`
    if (bcUpdates > 0) return `${total} updates (${bcUpdates} Basecamp)`
    return `${total} document edit${total === 1 ? '' : 's'}`
  }
  if (allRead) return `${total} document read${total === 1 ? '' : 's'}`

  // Mixed — compact breakdown, max 2 categories before falling back
  const counts: Record<string, number> = {}
  toolNames.forEach(n => {
    const label = getToolLabel(n)
    counts[label] = (counts[label] ?? 0) + 1
  })
  const parts = Object.entries(counts).map(([label, count]) => count > 1 ? `${label} ×${count}` : label)
  if (parts.length > 2) return `${total} operations`
  return parts.join(', ')
}

/** Summarise a group of tool messages into a single collapsible card. */
export function BatchToolMessages({ messages, onDocumentClick, activeDocumentId }: BatchToolMessagesProps) {
  const anyRunning = messages.some(m => !isToolRunTerminal(m.toolResult))
  const anyHardError = messages.some((m) => isHardToolFailure(m.toolResult))
  const anyAllowlistSoft = messages.some((m) => isAllowlistSoftFailure(m.toolResult))
  const [expanded, setExpanded] = useState(false)

  // Auto-expand while tools are still running so progress is visible; collapse once done
  const isExpanded = anyRunning || expanded

  const groupLabel = buildGroupLabel(messages)
  const headerIcon = batchHeaderIcon(messages.map(m => m.toolName))

  return (
    <div className="w-full min-w-0 -mt-0.5">
      <div className="pt-0.5">
        <div
          className={cn(
            'rounded-md border border-border/60 bg-muted/30 text-sm overflow-hidden',
            anyHardError && 'border-amber-500/20 bg-amber-500/[0.06]',
            !anyHardError && anyAllowlistSoft && 'border-border/60 bg-muted/20',
          )}
          data-testid={testIds.chatPane.toolGroupCard}
        >
          {/* Clickable header — always visible */}
          <button
            type="button"
            onClick={() => !anyRunning && setExpanded(e => !e)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
              !anyRunning && 'hover:bg-muted/60 cursor-pointer',
              anyRunning && 'cursor-default'
            )}
            data-testid={testIds.chatPane.toolGroupHeader}
          >
            <span className={cn('shrink-0 text-muted-foreground', anyHardError && 'text-amber-600 dark:text-amber-500')}>
              {anyRunning
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                : anyHardError
                  ? <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                  : headerIcon
              }
            </span>
            <span className="min-w-0 flex-1 text-xs font-medium text-foreground/80">{groupLabel}</span>
            {!anyRunning && (
              <ChevronRight
                className={cn(
                  'h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-150',
                  isExpanded && 'rotate-90'
                )}
                aria-hidden
              />
            )}
          </button>

          {/* Expandable rows */}
          {isExpanded && (
            <div
              className="border-t border-border/40 divide-y divide-border/30"
              data-testid={testIds.chatPane.toolGroupRows}
            >
              {messages.map((m, idx) => {
                const footnote = m.toolResult != null ? compactToolFootnote(m.toolName, m.toolResult) : null
                const isRunning = !isToolRunTerminal(m.toolResult)
                const rowFailure = toolResultFailureText(m.toolResult)
                const rowSoft = isSoftToolFailure(m.toolResult)
                return (
                  <div key={idx} className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground">
                    <span className="shrink-0 w-3">
                      {isRunning ? (
                        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                      ) : rowFailure ? (
                        rowSoft ? (
                          <AlertTriangle className="h-3 w-3 text-muted-foreground/60" aria-hidden />
                        ) : (
                          <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-500" aria-hidden />
                        )
                      ) : (
                        <Check className="h-3 w-3" aria-hidden />
                      )}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {isRunning ? (
                        <span className="italic opacity-60">{m.toolName ? getToolLabel(m.toolName) : 'Running…'}</span>
                      ) : rowFailure ? (
                        rowSoft ? (
                          <span className="text-muted-foreground/80 italic">
                            {softFailureDisplayText(m.toolResult)}
                          </span>
                        ) : (
                          <span className="text-amber-600 dark:text-amber-400">{rowFailure}</span>
                        )
                      ) : footnote ? (
                        <>
                          <span className="text-foreground/70">
                            {m.toolDisplayLabel || (m.toolName ? getToolLabel(m.toolName) : '—')}
                          </span>
                          <span className="mx-1 text-border/60">·</span>
                          <span className="opacity-70">{footnote}</span>
                        </>
                      ) : (
                        m.toolDisplayLabel || (m.toolName ? getToolLabel(m.toolName) : '—')
                      )}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface ToolMessageProps {
  message: Message
  onDocumentClick?: (docId: string, event?: React.MouseEvent) => void | Promise<void>
  activeDocumentId?: string | null
}

/** Derive StreamingOutputWindow props from tool result for create/update/append doc tools */
function getStreamingOutputProps(
  message: Message
): {
  title: string
  lines: string[]
  documentPath?: string
  documentUrl?: string
  documentId?: string
  linesAdded?: number
  linesRemoved?: number
} | null {
  const { toolName, toolResult } = message
  if (!toolResult?.success) return null

  if (toolName === 'createDocument') {
    return null
  }

  if (
    (toolName === 'updateDocument' || toolName === 'appendToDocument' || toolName === 'patchDocument' || toolName === 'moveSection') &&
    toolResult.type === 'proposed_edit' &&
    // Reloaded toolTrace proposals are compact (no bodies) - nothing to preview.
    typeof toolResult.proposed === 'string' &&
    toolResult.proposed.length > 0
  ) {
    const original = toolResult.original || ''
    const proposed = toolResult.proposed || ''
    const origLines = original.split('\n').length
    const propLines = proposed.split('\n').length
    const diff = propLines - origLines
    const fn = typeof toolResult.filename === 'string' ? toolResult.filename : undefined
    return {
      title:
        (typeof toolResult.displayPath === 'string' && toolResult.displayPath) ||
        pickDocumentResultDisplayTitle(
          typeof toolResult.title === 'string' ? toolResult.title : undefined,
          fn
        ),
      lines: proposed.split('\n'),
      documentPath: fn,
      documentUrl: typeof toolResult.url === 'string' ? toolResult.url : undefined,
      documentId: toolResult.documentId,
      linesAdded: diff > 0 ? diff : undefined,
      linesRemoved: diff < 0 ? -diff : undefined
    }
  }

  return null
}

function renderCompactCreateRow(
  message: Message,
  onDocumentClick?: (docId: string, event?: React.MouseEvent) => void | Promise<void>,
) {
  const tr = message.toolResult
  if (!tr?.success) return null
  return (
    <FileEditWidget
      fileName={typeof tr.filePath === 'string' ? tr.filePath : 'document.md'}
      displayPath={typeof tr.displayPath === 'string' ? tr.displayPath : undefined}
      url={typeof tr.url === 'string' ? tr.url : undefined}
      title={tr.title || tr.document?.title || undefined}
      additions={
        typeof message.outputContent === 'string'
          ? message.outputContent.split('\n').filter(Boolean).length
          : 0
      }
      deletions={0}
      status="accepted"
      requiresAcceptance={false}
      onOpen={
        onDocumentClick && tr.documentId
          ? async () => {
              window.dispatchEvent(new CustomEvent('document-created', {
                detail: {
                  documentId: tr.documentId,
                  filePath: tr.filePath,
                  title: tr.title,
                },
              }))
              if (tr.documentId) {
                window.dispatchEvent(new CustomEvent('document-updated', {
                  detail: { documentId: tr.documentId },
                }))
              }
              await onDocumentClick(tr.documentId)
            }
          : undefined
      }
    />
  )
}

export function ToolMessage({ message, onDocumentClick, activeDocumentId }: ToolMessageProps) {
  const outputProps = getStreamingOutputProps(message)
  const toolName = message.toolName
  const title =
    message.toolDisplayLabel ||
    (toolName ? getToolLabel(toolName) : message.content.replace(/\s*[✓✔]\s*$/, '').trim())
  const imageSearchResults =
    toolName === 'imageSearch' &&
    message.toolResult &&
    typeof message.toolResult === 'object' &&
    Array.isArray((message.toolResult as { results?: unknown }).results)
      ? ((message.toolResult as { results: ImageSearchHit[] }).results)
      : null
  const imageSearchQuery =
    toolName === 'imageSearch' &&
    message.toolResult &&
    typeof message.toolResult === 'object' &&
    typeof (message.toolResult as { query?: unknown }).query === 'string'
      ? ((message.toolResult as { query: string }).query)
      : undefined
  /** Successful getDocument is a silent context read — avoid a prominent "open" row that looks like the main answer. */
  const isQuietGetDocument =
    toolName === 'getDocument' && message.toolResult && typeof message.toolResult === 'object' && message.toolResult.success === true && !message.toolResult.error

  /** readToolResult is plumbing for spilled results — collapse to a one-liner. */
  const isQuietReadToolResult =
    toolName === 'readToolResult' && message.toolResult && typeof message.toolResult === 'object' && message.toolResult.success === true && !message.toolResult.error
  const hideVerboseJson =
    message.toolResult != null &&
    suppressVerboseToolDetailsJson(toolName, message.toolResult)
  const compactFootnote =
    hideVerboseJson && message.toolResult != null
      ? compactToolFootnote(toolName, message.toolResult)
      : null
  const failureText = toolResultFailureText(message.toolResult)
  const isEmptyToolResult =
    message.toolResult != null &&
    typeof message.toolResult === 'object' &&
    Object.keys(message.toolResult as object).length === 0
  const softFailure = isSoftToolFailure(message.toolResult)
  const showHardFailure = (Boolean(failureText) || isEmptyToolResult) && !softFailure
  const showSoftFailure = Boolean(failureText) && softFailure

  /** Successful createDocument - compact row only, no tall preview or Created card stack. */
  const isQuietCreateDocument =
    toolName === 'createDocument' &&
    message.toolResult &&
    typeof message.toolResult === 'object' &&
    message.toolResult.success === true

  /** Soft read misses (wrong slug, no captions) - same quiet rail as successful context reads. */
  const isQuietSoftRead =
    showSoftFailure &&
    (toolName === 'getDocument' ||
      toolName === 'readToolResult' ||
      toolName === 'fetchYoutubeTranscript')

  if (isQuietGetDocument || isQuietReadToolResult) {
    const label = isQuietReadToolResult ? 'Spill read' : 'Context read'
    return (
      <div className="w-full min-w-0 group -mt-0.5">
        <p
          className="pt-1 text-xs text-muted-foreground border-l-2 border-border/70 pl-2.5 max-w-prose leading-snug"
          data-testid={isQuietReadToolResult ? undefined : testIds.chatPane.messageToolQuietGetDocument}
        >
          <span className="font-medium text-foreground/65">{label}</span>
          {compactFootnote ? (
            <>
              <span className="mx-1 text-border">·</span>
              {compactFootnote}
            </>
          ) : null}
        </p>
      </div>
    )
  }

  if (isQuietSoftRead) {
    const label =
      toolName === 'fetchYoutubeTranscript'
        ? 'Fetching YouTube transcript'
        : toolName === 'readToolResult'
          ? 'Spill read'
          : title || 'Reading document'
    return (
      <div className="w-full min-w-0 group -mt-0.5" data-testid={testIds.chatPane.messageToolSoftFailure}>
        <div className="flex items-center gap-2.5 rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background/80 border border-border/50 text-muted-foreground/70">
            <AlertTriangle className="h-4 w-4" aria-hidden />
          </div>
          <span className="min-w-0 flex-1 font-medium text-foreground/75">{label}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground/80 italic max-w-prose leading-relaxed pl-0.5">
          {softFailureDisplayText(message.toolResult)}
        </p>
        {message.toolResult && (
          <details className="mt-0.5 text-sm group/details">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 py-0.5 text-xs text-muted-foreground/70 transition-colors hover:text-muted-foreground [&::-webkit-details-marker]:hidden">
              <ChevronRight
                className="h-3 w-3 shrink-0 transition-transform group-open/details:rotate-90"
                aria-hidden
              />
              Details
            </summary>
            <div className="mt-1.5 p-2.5 bg-muted/50 rounded-md text-xs max-w-2xl overflow-auto">
              <pre className="whitespace-pre-wrap font-mono break-words text-muted-foreground">
                {JSON.stringify(message.toolResult, null, 2)}
              </pre>
            </div>
          </details>
        )}
      </div>
    )
  }

  if (isQuietCreateDocument) {
    return (
      <div className="w-full min-w-0 group -mt-0.5 pt-0.5">
        {renderCompactCreateRow(message, onDocumentClick)}
      </div>
    )
  }

  return (
    <div className="w-full min-w-0 group -mt-0.5">
      {/* Content area */}
      <div className="flex-1 min-w-0 space-y-2 pt-0.5">
        {/* Compact tool run row (labels from getToolLabel; status via Lucide, not emoji) */}
        <div
          className={cn(
            'flex items-center gap-2.5 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm',
            showHardFailure && 'border-amber-500/25 bg-amber-500/[0.05]',
            showSoftFailure && 'border-border/50 bg-muted/20',
          )}
        >
          <div
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background/80 border border-border/50',
              message.toolResult?.success && 'text-green-500',
              showHardFailure && 'text-amber-600 dark:text-amber-500',
              showSoftFailure && 'text-muted-foreground',
            )}
          >
            {showHardFailure || isEmptyToolResult ? (
              <AlertTriangle className="h-4 w-4" aria-hidden />
            ) : showSoftFailure ? (
              <AlertTriangle className="h-4 w-4 text-muted-foreground/70" aria-hidden />
            ) : message.toolResult?.success ? (
              <CircleCheck className="h-4 w-4" aria-hidden />
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
            )}
          </div>
          <span className="min-w-0 flex-1 font-medium text-foreground/90">{title}</span>
          {!isToolRunTerminal(message.toolResult) && (
            <span className="shrink-0 text-xs text-muted-foreground">Running…</span>
          )}
        </div>
        {showSoftFailure && (
          <p className="text-xs text-muted-foreground/80 italic max-w-prose leading-relaxed">
            {softFailureDisplayText(message.toolResult)}
          </p>
        )}
        {showHardFailure && (
          <p className="text-xs text-amber-700/90 dark:text-amber-400/90 max-w-prose leading-relaxed">
            {failureText ||
              'Tool finished without a result payload (likely a server response shape bug). Retry or check API logs.'}
          </p>
        )}

        {/* StreamingOutputWindow: show created/updated content inline */}
        {outputProps && (
          <StreamingOutputWindow
            title={outputProps.title}
            lines={outputProps.lines}
            isStreaming={false}
            linesAdded={outputProps.linesAdded}
            linesRemoved={outputProps.linesRemoved}
            documentPath={outputProps.documentPath}
            documentUrl={outputProps.documentUrl}
            onOpenDocument={
              onDocumentClick && (outputProps.documentId || outputProps.documentPath)
                ? (pathOrId, e) => onDocumentClick(outputProps.documentId ?? pathOrId, e)
                : undefined
            }
            className="mt-2"
          />
        )}

      {/* Show createDocument hard-failure details (soft misses use the muted line above) */}
      {message.toolName === 'createDocument' && showHardFailure && (
        <>
          {message.toolResult &&
            message.toolResult.success !== true &&
            (message.toolResult.error || message.toolResult.message) ? (
            <div className="mt-2 px-3 py-2 bg-amber-500/8 border border-amber-500/20 rounded-md">
              <div className="text-sm font-medium text-amber-700 dark:text-amber-400">
                Failed to create document
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {message.toolResult.error || message.toolResult.message}
              </div>
            </div>
          ) : message.toolResult ? (
            <div className="mt-2 px-3 py-2 bg-muted/40 border border-border/60 rounded-md">
              <div className="text-sm font-medium text-muted-foreground">
                createDocument finished with unclear status
              </div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">
                {JSON.stringify(message.toolResult, null, 2)}
              </div>
            </div>
          ) : (
            <div className="mt-2 px-3 py-2 bg-muted/40 border border-border/60 rounded-md">
              <div className="text-sm font-medium text-muted-foreground">
                createDocument called but no toolResult
              </div>
            </div>
          )}
        </>
      )}

      {/* Show updateDocument/appendToDocument success details (card only when no StreamingOutputWindow) */}
      {(message.toolName === 'updateDocument' || message.toolName === 'appendToDocument') && message.toolResult?.success && message.toolResult?.type !== 'proposed_edit' && !message.proposedEdit && !outputProps && (
        <DocumentResultCard
          title={pickDocumentResultDisplayTitle(message.toolResult.title, message.toolResult.filename)}
          filePath={message.toolResult.filename || message.toolResult.filePath}
          displayPath={message.toolResult.displayPath}
          url={message.toolResult.url}
          documentId={message.toolResult.documentId}
          action={message.toolName === 'updateDocument' ? 'updated' : 'appended'}
          onOpen={async (docId) => {
            if (onDocumentClick) {
              // Trigger document-updated event to refresh search
              if (message.toolResult.documentId) {
                window.dispatchEvent(new CustomEvent('document-updated', {
                  detail: { documentId: message.toolResult.documentId }
                }));
              }
              // Open the document
              await onDocumentClick(docId);
            }
          }}
          className="mt-2"
        />
      )}

        {imageSearchResults && imageSearchResults.length > 0 && (
          <ImageSearchGallery
            query={imageSearchQuery}
            results={imageSearchResults}
            activeDocumentId={activeDocumentId}
          />
        )}

        {message.toolName === 'createQuizDocument' &&
          message.toolResult?.success &&
          typeof message.toolResult.documentId === 'string' && (
            <QuizCreatedCard
              quiz={{
                documentId: message.toolResult.documentId,
                filePath:
                  typeof message.toolResult.filePath === 'string'
                    ? message.toolResult.filePath
                    : null,
                title:
                  typeof message.toolResult.title === 'string' ? message.toolResult.title : null,
                slug: typeof message.toolResult.slug === 'string' ? message.toolResult.slug : null,
                questionCount:
                  typeof message.toolResult.questionCount === 'number'
                    ? message.toolResult.questionCount
                    : null,
              }}
              onOpen={onDocumentClick}
            />
          )}

        {(compactFootnote || hideVerboseJson) &&
          !imageSearchResults?.length &&
          message.toolName !== 'createQuizDocument' && (
          <p className="text-xs text-muted-foreground border-l-2 border-border/80 pl-2 py-0.5 max-w-prose leading-snug">
            {compactFootnote ?? 'Completed (raw payload hidden in chat for readability).'}
          </p>
        )}

        {/* Collapsible raw tool payload — skipped for huge / redundant tools (triage analyze readability). */}
        {message.toolResult && !hideVerboseJson && !isEmptyToolResult && !isQuietSoftRead && (
          <details className="text-sm group/details">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground [&::-webkit-details-marker]:hidden">
              <ChevronRight
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground/80 transition-transform group-open/details:rotate-90"
                aria-hidden
              />
              {message.toolResult.success
                ? 'Technical details'
                : softFailure
                  ? 'Details'
                  : 'Error details'}
            </summary>
            <div className="mt-2 p-3 bg-muted rounded-md text-xs max-w-2xl overflow-auto">
              <pre className="whitespace-pre-wrap font-mono break-words">
                {/*
                  Always show full toolResult JSON in details. Previously, when `error` was a string
                  we only showed that string — so structured fields (reason, hint, requestedDocumentId)
                  from the API were invisible. Failures stay readable: summary line above + full payload here.
                */}
                {JSON.stringify(message.toolResult, null, 2)}
              </pre>
            </div>
          </details>
        )}
      </div>
    </div>
  )
}

